import { useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, Phone, Mail } from "lucide-react";
import Reveal from "./Reveal";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useContent } from "../content";
import { api, formatApiError } from "../api";

const EMPTY = { name: "", email: "", phone: "", category: "", service: "", message: "" };

export default function QuoteForm() {
  const CONTACT = useContent().contact;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name || name.length < 2) return toast.error("Merci d'indiquer votre nom.");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Adresse email invalide.");
    if (!form.category) return toast.error("Choisissez une catégorie.");
    if (!message || message.length < 5) return toast.error("Votre message doit contenir au moins 5 caractères.");

    setLoading(true);
    try {
      await api.post("/quotes", { ...form, name, email, message });
      setDone(true);
      toast.success("Votre demande de devis a bien été envoyée !");
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail ? formatApiError(detail) : (err?.message || "Une erreur est survenue. Réessayez ou appelez-nous.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" className="relative bg-[#0a0a0a] py-24 sm:py-32" data-testid="quote-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50">
            Parlons de votre projet
          </span>
          <h2 className="mt-4 font-head text-4xl font-semibold uppercase tracking-tight text-white sm:text-5xl md:text-6xl">
            Demandez votre <span className="text-gradient">devis gratuit</span>
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-white/60">
            Décrivez votre besoin en maintenance ou en événementiel. Nous vous
            répondons rapidement avec une proposition sur-mesure.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`tel:${CONTACT.phone_raw}`}
              className="group flex items-center gap-4 text-white"
              data-testid="quote-phone-link"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md border border-white/10 bg-white/5 text-[#ff4d4d]">
                <Phone size={18} />
              </span>
              <span className="text-lg font-medium transition-colors group-hover:text-[#ff8c8c]">
                {CONTACT.phone}
              </span>
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              className="group flex items-center gap-4 text-white"
              data-testid="quote-email-link"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md border border-white/10 bg-white/5 text-[#ff4d4d]">
                <Mail size={18} />
              </span>
              <span className="text-lg font-medium transition-colors group-hover:text-[#ff8c8c]">
                {CONTACT.email}
              </span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative rounded-lg border border-white/10 bg-[#0f0f0f] p-1">
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-[#e63946]/20 via-transparent to-transparent" />
            <div className="relative rounded-md bg-[#0c0c0c] p-6 sm:p-8">
              {done ? (
                <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="quote-success">
                  <CheckCircle2 size={54} className="text-[#ff4d4d]" />
                  <h3 className="mt-6 font-head text-2xl font-medium uppercase tracking-tight text-white">
                    Merci !
                  </h3>
                  <p className="mt-3 max-w-xs text-sm font-light text-white/60">
                    Votre demande a bien été reçue. Nous revenons vers vous très vite.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-8 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-black"
                    data-testid="quote-new-btn"
                  >
                    Nouvelle demande
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-xs uppercase tracking-wider text-white/70">
                        Nom complet *
                      </Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Jean Dupont"
                        className="border-white/10 bg-black/50 text-white placeholder:text-white/30 focus-visible:border-[#e63946] focus-visible:ring-[#e63946]"
                        data-testid="quote-input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-white/70">
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="06 12 34 56 78"
                        className="border-white/10 bg-black/50 text-white placeholder:text-white/30 focus-visible:border-[#e63946] focus-visible:ring-[#e63946]"
                        data-testid="quote-input-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-wider text-white/70">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="vous@email.fr"
                      className="border-white/10 bg-black/50 text-white placeholder:text-white/30 focus-visible:border-[#e63946] focus-visible:ring-[#e63946]"
                      data-testid="quote-input-email"
                    />
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-white/70">Catégorie *</Label>
                      <Select
                        value={form.category}
                        onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
                      >
                        <SelectTrigger
                          className="border-white/10 bg-black/50 text-white focus:border-[#e63946] focus:ring-[#e63946]"
                          data-testid="quote-select-category"
                        >
                          <SelectValue placeholder="Choisir..." />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-[#111] text-white">
                          <SelectItem value="maintenance" data-testid="cat-maintenance">Maintenance des bâtiments</SelectItem>
                          <SelectItem value="evenementiel" data-testid="cat-evenementiel">Événementiel</SelectItem>
                          <SelectItem value="autre" data-testid="cat-autre">Autre demande</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-xs uppercase tracking-wider text-white/70">
                        Prestation
                      </Label>
                      <Input
                        id="service"
                        value={form.service}
                        onChange={set("service")}
                        placeholder="Ex : sonorisation, plomberie…"
                        className="border-white/10 bg-black/50 text-white placeholder:text-white/30 focus-visible:border-[#e63946] focus-visible:ring-[#e63946]"
                        data-testid="quote-input-service"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-xs uppercase tracking-wider text-white/70">
                      Votre message *
                    </Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={set("message")}
                      rows={4}
                      placeholder="Décrivez votre projet…"
                      className="border-white/10 bg-black/50 text-white placeholder:text-white/30 focus-visible:border-[#e63946] focus-visible:ring-[#e63946]"
                      data-testid="quote-input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-gradient px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                    data-testid="quote-submit-btn"
                  >
                    {loading ? "Envoi en cours…" : "Envoyer ma demande"}
                    {!loading && <Send size={16} className="transition-transform group-hover:translate-x-0.5" />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
